import React, { useState } from "react";
import { ChevronDown, Sliders, Save } from "lucide-react";
import DatePicker from "../../../../../components/DatePicker/DatePicker";
import { REPORT_PERIOD_OPTIONS } from "../constants";
import type { ReportPeriod } from "../types";

interface Props {
  period: ReportPeriod;
  from: string;
  to: string;
  onPeriodChange: (p: ReportPeriod) => void;
  onFromChange: (iso: string) => void;
  onToChange: (iso: string) => void;
  onCustomize: () => void;
  onSaveAs: () => void;
}

export default function QuickReportControls({
  period, from, to, onPeriodChange, onFromChange, onToChange, onCustomize, onSaveAs,
}: Props) {
  const [periodOpen, setPeriodOpen] = useState(false);
  const activeLabel = REPORT_PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? "Report period";

  return (
    <div className="flex flex-wrap items-end gap-4 justify-between mb-5">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1 min-w-[180px] relative">
          <label className="text-[10.5px] font-bold text-ink3 uppercase tracking-wider">Report period</label>
          <button
            type="button"
            onClick={() => setPeriodOpen((o) => !o)}
            className="h-9 border border-[#D8DEE6] rounded-lg px-3 flex items-center justify-between text-[13px] text-ink bg-white"
          >
            {activeLabel}
            <ChevronDown className="w-3.5 h-3.5 text-ink3" />
          </button>
          {periodOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setPeriodOpen(false)} />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#D8DEE6] rounded-lg shadow-lg z-40 py-1 max-h-72 overflow-auto">
                {REPORT_PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { onPeriodChange(opt.value); setPeriodOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-[12.5px] hover:bg-[#EEF4FB] ${period === opt.value ? "bg-[#F0F6FC] font-semibold text-ink" : "text-ink2"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="text-[10.5px] font-bold text-ink3 uppercase tracking-wider">From</label>
          <DatePicker value={from} onChange={onFromChange} />
        </div>

        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="text-[10.5px] font-bold text-ink3 uppercase tracking-wider">To</label>
          <DatePicker value={to} onChange={onToChange} />
        </div>
      </div>

      <div className="flex gap-2.5 items-center">
        <button
          type="button"
          onClick={onCustomize}
          className="h-9 px-4 border border-[#D8DEE6] rounded-[10px] text-[12.5px] font-semibold text-ink bg-white inline-flex items-center gap-1.5"
        >
          <Sliders className="w-3.5 h-3.5" />
          Customize
        </button>
        <button
          type="button"
          onClick={onSaveAs}
          className="h-9 px-4 border border-ink rounded-[10px] text-[12.5px] font-semibold text-white bg-ink inline-flex items-center gap-1.5"
        >
          <Save className="w-3.5 h-3.5" />
          Save as
        </button>
      </div>
    </div>
  );
}